import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import JsonLd, { articleLd, faqLd, breadcrumbLd } from "../components/JsonLd";
import { ArrowLeft, AlertCircle } from "lucide-react";

export default function Article() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  useEffect(() => { api.get(`/articles/${slug}`).then(({ data }) => setA(data)).catch(() => {}); }, [slug]);

  if (!a) return <div className="hc-container py-20 text-center text-slate-500">Loading…</div>;

  return (
    <>
      <SEO title={a.title} description={a.summary} />
      <JsonLd id="ld-article" data={articleLd(a, slug)} />
      {a.faqs?.length > 0 && <JsonLd id="ld-faq" data={faqLd(a.faqs)} />}
      <JsonLd id="ld-breadcrumb" data={breadcrumbLd([
        { name: "Home", path: "/" },
        { name: "Knowledge Centre", path: "/knowledge-centre" },
        { name: a.title, path: `/article/${slug}` },
      ])} />
      <div className="hc-container py-10">
        <Link to="/knowledge-centre" data-testid="back-to-kc" className="inline-flex items-center gap-1.5 text-[#0369A1] font-medium text-sm"><ArrowLeft size={16} /> Knowledge Centre</Link>
      </div>
      <article className="hc-container max-w-3xl pb-16">
        <div className="text-[12px] text-[#0F766E] font-semibold uppercase tracking-wider">{a.category}</div>
        <h1 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 leading-tight mt-2">{a.title}</h1>
        <p className="mt-5 text-slate-700 text-[1.08rem] leading-relaxed">{a.summary}</p>
        <div className="mt-7 prose prose-slate text-slate-700 leading-relaxed whitespace-pre-line text-[16.5px]">{a.content}</div>

        {a.faqs?.length > 0 && (
          <div className="mt-10">
            <h2 className="font-serif text-2xl font-semibold text-slate-900">Frequently asked questions</h2>
            <div className="mt-5 space-y-4">
              {a.faqs.map((f, i) => (
                <details key={i} data-testid={`faq-${i}`} className="bg-white border border-slate-200 rounded-xl p-5">
                  <summary className="cursor-pointer font-semibold text-slate-900 text-[16px]">{f.q}</summary>
                  <div className="mt-2.5 text-slate-700 text-[15px] leading-relaxed">{f.a}</div>
                </details>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 bg-amber-50 border border-amber-200 rounded-xl p-5 text-amber-900 text-[14.5px] flex gap-3">
          <AlertCircle size={20} className="shrink-0 mt-0.5" />
          <div><strong>Medical disclaimer:</strong> This information is for awareness and education only. It does not replace consultation with a qualified ENT surgeon, audiologist or medical professional. Cochlear implant eligibility must be determined after clinical evaluation and diagnostic testing.</div>
        </div>

        <div className="mt-8 text-[13px] text-slate-500">Reviewed by {a.reviewer} · Last reviewed: {a.last_reviewed?.slice(0, 10)}</div>
      </article>
    </>
  );
}
